import { page } from '@/api/common'
import { getTaskList, getTaskCode, getTaskExchange } from '@/api/event'
import { showNotice } from '@/utils/tool'

/**
 * 存款頁「可報名的活動優惠」：進頁即載入清單，卡片／查看優惠碼彈窗共用同一份資料與報名流程。
 * 手機與 PC 各有一個 promo-code 元件，資料放在頁面層避免重複請求與狀態不同步。
 */
export default {
  data() {
    return {
      promoLoaded: false,
      promoAvailable: [], // brand 的 task-code 清單（不濾掉已報名，卡片依報名紀錄顯示已報名態）
      promoRegistered: [],
      promoExpired: [],
      promoDoneCodes: [], // 本次在頁面上報名成功的代碼
      promoConfirmCard: null,
      promoSubmitting: false,
      firstDepositText: '',
      firstDepositUrl: ''
    }
  },
  computed: {
    hasPromoData() {
      return this.promoAvailable.length > 0 || this.promoRegistered.length > 0 || this.promoExpired.length > 0
    },
    // 會員有報名紀錄的代碼（task-list 內不論 activate_no 為 '0' 或 UUID）＋本次在頁面報名成功的代碼
    promoEnteredCodes() {
      return [...this.promoRegistered, ...this.promoExpired].map(c => c.code).concat(this.promoDoneCodes)
    },
    // 狀態優先序：有報名紀錄 → 已報名；無紀錄且已首存（限首存優惠）→ 無法報名提示卡；其餘 → 可報名
    promoCards() {
      const hasFirstDeposit = this.memberInfo.first_deposit_at !== 0
      return this.promoAvailable.map(item => {
        const isFirstDeposit = item.module_name === 'FirstDeposit'
        const done = this.promoEnteredCodes.includes(item.code)
        return {
          id: item.id,
          code: item.code,
          endAt: item.end_at,
          name: isFirstDeposit ? this.firstDepositText : '',
          done,
          ineligible: !done && isFirstDeposit && hasFirstDeposit
        }
      })
    },
    promoAvailableCount() {
      return this.promoCards.filter(card => !card.done && !card.ineligible).length
    },
    // 查看優惠碼彈窗的「可報名」：有報名紀錄的不列入（紀錄本身列在已報名／已結束區）
    promoModalCoupons() {
      return this.promoAvailable.filter(item => !this.promoEnteredCodes.includes(item.code))
    },
    // 彈窗「已報名」：與頁面一致——activate_no 為 '0'，或活動仍在 task-code 清單內的紀錄（不論 activate_no）
    promoModalRegistered() {
      return [...this.promoRegistered, ...this.promoExpired.filter(c => this.findPromoTask(c.code))]
        .map(this.withPromoExpire)
    },
    // 彈窗「已結束」：activate_no 非 '0' 且活動已不在 task-code 清單內
    promoModalExpired() {
      return this.promoExpired.filter(c => !this.findPromoTask(c.code)).map(this.withPromoExpire)
    }
  },
  created() {
    this.fetchPromoData()
  },
  methods: {
    fetchPromoData() {
      const p1 = page({ type: 'first_deposit' }).then(response => {
        const data = response.data.data
        if (data && data.setting) {
          this.firstDepositText = data.content
          this.firstDepositUrl = data.setting.url
        }
      })

      const p2 = Promise.all([this.fetchPromoTaskList(), getTaskCode()]).then(([, res]) => {
        this.promoAvailable = res.data.data.list
      })

      Promise.all([p1, p2]).catch(() => {}).finally(() => {
        this.promoLoaded = true
      })
    },
    fetchPromoTaskList() {
      return getTaskList().then(response => {
        const list = response.data.data.list
        this.promoRegistered = list.filter(c => c.activate_no === '0')
        this.promoExpired = list.filter(c => c.activate_no !== '0')
      })
    },
    findPromoTask(code) {
      return this.promoAvailable.find(t => t.code === code)
    },
    // 有效期限與頁面卡片一致取活動結束日；活動已不在 task-code 清單（已結束）時不顯示日期
    withPromoExpire(record) {
      const task = this.findPromoTask(record.code)
      return { ...record, expire_at: task ? task.end_at : null }
    },
    openPromoConfirm(card) {
      this.promoConfirmCard = card
    },
    confirmPromoRegister() {
      const card = this.promoConfirmCard
      this.promoConfirmCard = null
      if (card) this.registerPromo(card.code)
    },
    registerPromo(taskCode) {
      if (this.promoSubmitting) return Promise.resolve(false)
      this.promoSubmitting = true

      return getTaskExchange({ task_code: taskCode })
        .then((response) => {
          const { code, message } = response.data.data
          if (code) {
            const i18nKey = 'Bao.apply_coupon_' + code
            showNotice(this.$te(i18nKey) ? this.$t(i18nKey) : message)
          }

          // 1=成功、7=已報名過：兩者都代表此優惠已是報名狀態
          if (code === 1 || code === 7) {
            if (!this.promoDoneCodes.includes(taskCode)) this.promoDoneCodes.push(taskCode)
            this.fetchPromoTaskList()
            return true
          }
          return false
        })
        .catch((error) => {
          showNotice(error.response?.data?.msg)
          return false
        })
        .finally(() => {
          this.promoSubmitting = false
        })
    }
  }
}
